import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { TrendingUp, AlertTriangle, DollarSign, Clock } from 'lucide-react';

const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function Reports() {
  const [dashboard, setDashboard] = useState(null);
  const [profitability, setProfitability] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, pRes] = await Promise.all([
        api.get('/reports/dashboard'),
        api.get('/reports/event-profitability'),
      ]);
      setDashboard(dRes.data);
      setProfitability(pRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !dashboard) {
    return <div className="min-h-screen bg-slate-50"><Sidebar /><main className="md:ml-64 p-8"><p>Loading…</p></main></div>;
  }

  const stats = [
    { label: 'Clients', value: dashboard.clients_count, color: 'text-slate-700', icon: null },
    { label: 'Events', value: dashboard.events_count, color: 'text-slate-700', icon: null },
    { label: 'Quotes (open)', value: fmtMoney(dashboard.quotes_open_total), color: 'text-blue-700', icon: <TrendingUp className="w-4 h-4" /> },
    { label: 'Quotes (won)', value: fmtMoney(dashboard.quotes_won_total), color: 'text-emerald-700', icon: <TrendingUp className="w-4 h-4" /> },
    { label: 'Invoiced', value: fmtMoney(dashboard.invoiced_total), color: 'text-slate-800', icon: <DollarSign className="w-4 h-4" /> },
    { label: 'Paid', value: fmtMoney(dashboard.paid_total), color: 'text-emerald-700', icon: <DollarSign className="w-4 h-4" /> },
    { label: 'Outstanding', value: fmtMoney(dashboard.outstanding_total), color: 'text-amber-700', icon: <AlertTriangle className="w-4 h-4" /> },
    { label: 'Overdue', value: fmtMoney(dashboard.overdue_total), color: 'text-rose-700', icon: <AlertTriangle className="w-4 h-4" /> },
    { label: 'Hours logged', value: dashboard.hours_logged.toFixed(1), color: 'text-slate-700', icon: <Clock className="w-4 h-4" /> },
    { label: 'Labour cost', value: fmtMoney(dashboard.labour_cost), color: 'text-amber-700', icon: <DollarSign className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-7 h-7 text-emerald-600" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Reports</h1>
              <p className="text-sm text-slate-500">Pipeline, cash flow and event profitability.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {stats.map((s) => (
              <Card key={s.label} className="glass-card p-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1">{s.icon}{s.label}</p>
                <p className={`text-lg md:text-xl font-bold ${s.color}`}>{s.value}</p>
              </Card>
            ))}
          </div>

          <Card className="glass-card overflow-hidden mb-6">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-slate-900">Event profitability</h2>
              <p className="text-xs text-slate-500">Invoice totals minus labour cost (timesheets), per event.</p>
            </div>
            {profitability.length === 0 ? (
              <p className="p-8 text-center text-slate-500 text-sm">No event data yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-xs text-slate-600 text-left">
                  <tr><th className="px-4 py-2">Event</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Quote</th><th className="px-4 py-2">Invoiced</th><th className="px-4 py-2">Paid</th><th className="px-4 py-2">Hours</th><th className="px-4 py-2">Cost</th><th className="px-4 py-2">Margin</th><th className="px-4 py-2">%</th><th></th></tr>
                </thead>
                <tbody>
                  {profitability.map((p) => (
                    <tr key={p.event_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2">{p.event_name}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{p.date}</td>
                      <td className="px-4 py-2">{fmtMoney(p.quote_amount)}</td>
                      <td className="px-4 py-2">{fmtMoney(p.invoiced)}</td>
                      <td className="px-4 py-2 text-emerald-700">{fmtMoney(p.paid)}</td>
                      <td className="px-4 py-2">{p.hours.toFixed(1)}</td>
                      <td className="px-4 py-2 text-amber-700">{fmtMoney(p.cost)}</td>
                      <td className={`px-4 py-2 font-semibold ${p.margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtMoney(p.margin)}</td>
                      <td className={`px-4 py-2 text-xs ${p.margin_pct >= 30 ? 'text-emerald-700' : p.margin_pct >= 0 ? 'text-amber-700' : 'text-rose-700'}`}>{p.margin_pct.toFixed(1)}%</td>
                      <td className="px-4 py-2"><Link to={`/events/${p.event_id}`} className="text-emerald-600 text-xs">→</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
